// Invoice Log (IT Budget, SPEC.md Module 1): every receipt and vendor invoice
// the Invoice Collector logged, its run status, and IT's fixes to a row.

// REACT IMPORTS
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

// MSAL
import { useMsal } from "@azure/msal-react";

// MUI COMPONENTS
import { Alert, Button, CircularProgress, LinearProgress, MenuItem, Snackbar, Table, TableBody, TableCell, TableHead, TableRow, TextField } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import UploadFileIcon from "@mui/icons-material/UploadFile";

// CUSTOM COMPONENTS
import InvoiceDialog from "./InvoiceDialog";
import UploadInvoiceDialog from "./UploadInvoiceDialog";
import TanStackTablePagination from "../../Misc/Table/TanStackTablePagination/TanStackTablePagination";
import usePagedRows from "../../Proposals/ProductLibrary/usePagedRows";
import { SortableHeaderCell, useSortedRows } from "../../Admin/TimeOffConsole/sortableTable";

// CONTEXT, API, CONSTANTS
import { UserContext } from "../../../context/UserContext";
import { getAPICall, postAPICall } from "../../../config/apiCalls";
import { MATCH_PILLS, STATUS_PILLS, apiError, fmtDateTime, fmtDay, fmtUSD, hasAccess, pillStyle } from "../itBudgetUtils";

// CSS
import "../ITBudget.css";

const TABS = [
	{ id: "all", label: "All" },
	{ id: "needs_review", label: "Needs Review" },
	{ id: "unmatched", label: "Unmatched" },
	{ id: "excluded", label: "Excluded" },
];
const RECEIVED = [
	{ value: "7", label: "Last 7 Days" },
	{ value: "30", label: "Last 30 Days" },
	{ value: "90", label: "Last 90 Days" },
	{ value: "year", label: "This Year" },
	{ value: "", label: "All Time" },
];
const SORT_GETTERS = {
	received: (r) => r.received || "",
	invoice_date: (r) => r.invoice_date || "",
	vendor: (r) => r.vendor || "",
	invoice_number: (r) => r.invoice_number || "",
	total: (r) => Number(r.total_amount),
	tax: (r) => (r.tax_amount === null ? null : Number(r.tax_amount)),
	paid_by: (r) => r.card_label || r.payment_method || "",
	status: (r) => r.status || "",
};

const FilterSelect = ({ label, value, onChange, children }) => (
	<TextField select size="small" label={label} value={value} onChange={(e) => onChange(e.target.value)} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
		{children}
	</TextField>
);

// While a run is in progress the page asks for its status this often.
const RUN_POLL_MS = 10 * 1000;

// "4:05" from seconds.
const fmtElapsed = (seconds) => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;

const InvoiceLog = ({ access }) => {
	const { instance, accounts } = useMsal();
	const employeeID = useContext(UserContext);
	const canEdit = hasAccess(access, "invoices", "full");

	const [tab, setTab] = useState("all");
	const [filters, setFilters] = useState({ vendor: "", received: "30", mailbox: "", paid_by: "" });
	const [data, setData] = useState({ rows: [], counts: {} });
	const [kpis, setKpis] = useState(null);
	const [run, setRun] = useState(null);
	const [lookups, setLookups] = useState({ vendors: [], paymentMethods: [], mailboxes: [] });
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [toast, setToast] = useState("");
	const [openId, setOpenId] = useState(null);
	const [uploadOpen, setUploadOpen] = useState(false);
	const [starting, setStarting] = useState(false);
	const [elapsed, setElapsed] = useState(0); // seconds since the running run began, ticking locally
	const wasRunning = useRef(false);

	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "IT Budget - Invoice Log",
			employeeID: employeeID,
			notes: "Accessed the IT Budget Invoice Log page",
		}).catch(() => {});
		getAPICall(instance, accounts[0], "/api/it-budget/lookups")
			.then((res) => setLookups(res.data))
			.catch(() => {});
	}, []);

	const load = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const [list, k, status] = await Promise.all([
				getAPICall(instance, accounts[0], "/api/it-budget/invoices", { tab, ...filters }),
				getAPICall(instance, accounts[0], "/api/it-budget/invoices/kpis"),
				getAPICall(instance, accounts[0], "/api/it-budget/run-status"),
			]);
			setData(list.data);
			setKpis(k.data);
			setRun(status.data);
		} catch (err) {
			setError(apiError(err, "Couldn't load the Invoice Log."));
		} finally {
			setLoading(false);
		}
	}, [instance, accounts, tab, filters]);

	useEffect(() => {
		load();
	}, [load]);

	const { sorted, sortKey, sortDir, onSort } = useSortedRows(data.rows, SORT_GETTERS, "received", "desc", ["received", "invoice_date", "total", "tax"]);
	const { pageRows, table: pager, pagination } = usePagedRows(sorted, [tab, filters.vendor, filters.received, filters.mailbox, filters.paid_by, sortKey, sortDir].join("|"));

	const loadRunStatus = useCallback(async () => {
		const res = await getAPICall(instance, accounts[0], "/api/it-budget/run-status");
		setRun(res.data);
		return res.data;
	}, [instance, accounts]);

	// While a run is in progress: tick the elapsed time each second and check its status every 10 seconds.
	// When its summary arrives, reload the list and say how it went.
	const running = run?.running;
	useEffect(() => {
		if (!running) return undefined;
		setElapsed(running.ageSeconds);
		const began = Date.now() - running.ageSeconds * 1000;
		const tick = setInterval(() => setElapsed((Date.now() - began) / 1000), 1000);
		const poll = setInterval(() => loadRunStatus().catch(() => {}), RUN_POLL_MS);
		return () => {
			clearInterval(tick);
			clearInterval(poll);
		};
	}, [running?.since, loadRunStatus]);

	useEffect(() => {
		if (wasRunning.current && run && !run.running) {
			const done = run.latest;
			setToast(
				done
					? `Invoice Collector finished: ${done.found} found, ${done.logged} logged, ${done.duplicate} duplicate, ${done.failed} failed.`
					: "The Invoice Collector stopped without a summary; see Automation Logs.",
			);
			load();
		}
		wasRunning.current = Boolean(run?.running);
	}, [run]);

	const runNow = async () => {
		setStarting(true);
		try {
			await postAPICall(instance, accounts[0], "/api/it-budget/run-now", {});
			await loadRunStatus();
		} catch (err) {
			setError(apiError(err, "Couldn't start the collector."));
		} finally {
			setStarting(false);
		}
	};

	const openFile = async (row, e) => {
		e.stopPropagation();
		try {
			const res = await getAPICall(instance, accounts[0], `/api/it-budget/invoices/${row.id}/file`);
			window.open(res.data.url, "_blank", "noopener");
		} catch (err) {
			setError(apiError(err, "Couldn't open the file."));
		}
	};

	const latest = run?.latest;
	const columns = useMemo(
		() => [
			{ key: "received", label: "Received" },
			{ key: "invoice_date", label: "Invoice Date" },
			{ key: "vendor", label: "Vendor" },
			{ key: "invoice_number", label: "Invoice #" },
			{ key: "total", label: "Total", align: "right" },
			{ key: "tax", label: "Tax", align: "right" },
			{ key: "paid_by", label: "Paid By" },
			{ key: null, label: "Budget Line" },
			{ key: "status", label: "Status" },
			{ key: null, label: "Match" },
			{ key: null, label: "File" },
		],
		[],
	);

	return (
		<div className="itBudgetPage">
			<div className="itBudgetHeader">
				<div>
					<div className="itBudgetBreadcrumb">IT Budget / Invoice Log</div>
					<div className="itBudgetTitle">Invoice Log</div>
					<div className="itBudgetSubtitle">Every receipt and vendor invoice the Invoice Collector captures, with its PDF in Dropbox.</div>
				</div>
				{canEdit && (
					<Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => setUploadOpen(true)}>
						Upload Invoice
					</Button>
				)}
			</div>

			{/* Run status strip */}
			<div className="itBudgetCard itBudgetStatusStrip" style={{ position: "relative", overflow: "hidden" }}>
				{running ? (
					<CircularProgress size={22} aria-label="Invoice Collector running" />
				) : run && !run.alert ? (
					<CheckCircleIcon style={{ color: "#2e7d32" }} />
				) : (
					<ErrorIcon style={{ color: "#c62828" }} />
				)}
				<div className="itBudgetStatusText">
					<b>Invoice Collector · AWS Lambda{latest?.shadow ? " · shadow run" : ""}</b>
					{running && (
						<span style={{ color: "#184c93" }} role="status">
							<b>Running</b> · started {fmtDateTime(running.since)}
							{running.by === "schedule" ? " by the schedule" : ` by ${running.by}`} · {fmtElapsed(elapsed)} elapsed · the summary appears here when it
							finishes (usually a few minutes)
						</span>
					)}
					{!running && run?.unfinished && (
						<span style={{ color: "#b45309", display: "inline-flex", alignItems: "center", gap: 4 }}>
							<WarningAmberIcon fontSize="inherit" /> The run started {fmtDateTime(run.unfinished.since)}
							{run.unfinished.by === "schedule" ? "" : ` by ${run.unfinished.by}`} never wrote a summary; see Automation Logs.
						</span>
					)}
					{latest ? (
						<span>
							{fmtDateTime(latest.created)} · <span className="itBudgetMono">{latest.runId}</span> · {latest.found} found · {latest.logged} logged ·{" "}
							{latest.duplicate} duplicate · {latest.failed} failed{latest.stoppedEarly ? " · stopped early" : ""}
						</span>
					) : (
						<span>No run recorded yet.</span>
					)}
					<span className="itBudgetMuted">
						{run?.alert && latest ? `No successful run in the last ${run.alertAfterHours} hours, or the latest run failed. ` : ""}
						{run?.deniedMailboxes?.length ? `Denied: ${run.deniedMailboxes.join(", ")}. ` : ""}
						Schedule: {run?.schedule || "—"}
					</span>
				</div>
				<Button component={RouterLink} to="/logs/automation/dashboard" size="small">
					Automation Logs
				</Button>
				{canEdit && (
					<Button variant="outlined" startIcon={starting ? <CircularProgress size={16} /> : <PlayArrowIcon />} disabled={starting || Boolean(running)} onClick={runNow}>
						{running ? "Running…" : "Run Collector Now"}
					</Button>
				)}
				{running && <LinearProgress style={{ position: "absolute", left: 0, right: 0, bottom: 0 }} />}
			</div>

			{/* KPI cards */}
			<div className="itBudgetKpiGrid">
				{[
					{ label: "Logged this month", value: kpis?.month.count, sub: fmtUSD(kpis?.month.total) },
					{ label: "Unmatched to a card charge", value: kpis?.unmatched.count, sub: `${fmtUSD(kpis?.unmatched.total)} posted provisionally`, color: "#184c93" },
					{ label: "Needs Review", value: kpis?.needsReview.count, sub: "Read failed, a check failed, or unknown vendor", color: kpis?.needsReview.count ? "#b53943" : undefined },
					{ label: "Logged year to date", value: fmtUSD(kpis?.year.total), sub: `${kpis?.year.count ?? "—"} invoices` },
				].map((card) => (
					<div className="itBudgetCard" key={card.label}>
						<div className="itBudgetOverline">{card.label}</div>
						<div className="itBudgetKpiValue" style={{ color: card.color }}>
							{card.value ?? "—"}
						</div>
						<div className="itBudgetMuted">{card.sub}</div>
					</div>
				))}
			</div>

			<div className="itBudgetCard" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
				<div className="itBudgetTabs">
					{TABS.map((t) => (
						<button key={t.id} className={`itBudgetTab${tab === t.id ? " itBudgetTab--active" : ""}`} onClick={() => setTab(t.id)}>
							{t.label}
							{t.id !== "all" && data.counts[t.id] !== undefined ? ` (${data.counts[t.id]})` : ""}
						</button>
					))}
				</div>
				<div className="itBudgetFilters">
					<FilterSelect label="Vendor" value={filters.vendor} onChange={(v) => setFilters({ ...filters, vendor: v })}>
						<MenuItem value="">All Vendors</MenuItem>
						{lookups.vendors.map((v) => (
							<MenuItem key={v.id} value={v.id}>
								{v.name}
							</MenuItem>
						))}
					</FilterSelect>
					<FilterSelect label="Received" value={filters.received} onChange={(v) => setFilters({ ...filters, received: v })}>
						{RECEIVED.map((r) => (
							<MenuItem key={r.value} value={r.value}>
								{r.label}
							</MenuItem>
						))}
					</FilterSelect>
					<FilterSelect label="Mailbox" value={filters.mailbox} onChange={(v) => setFilters({ ...filters, mailbox: v })}>
						<MenuItem value="">All Mailboxes</MenuItem>
						{lookups.mailboxes.map((m) => (
							<MenuItem key={m} value={m}>
								{m}
							</MenuItem>
						))}
						<MenuItem value="manual">Manual upload</MenuItem>
					</FilterSelect>
					<FilterSelect label="Paid By" value={filters.paid_by} onChange={(v) => setFilters({ ...filters, paid_by: v })}>
						<MenuItem value="">All Methods</MenuItem>
						{lookups.paymentMethods.map((p) => (
							<MenuItem key={p.id} value={p.id}>
								{p.display_name}
							</MenuItem>
						))}
					</FilterSelect>
				</div>

				{error && <div className="itBudgetError">{error}</div>}
				{loading ? (
					<div className="itBudgetEmpty">
						<CircularProgress />
					</div>
				) : !sorted.length ? (
					<div className="itBudgetEmpty">No invoices match these filters.</div>
				) : (
					<>
						<div className="itBudgetTableContainer">
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										{columns.map((c) =>
											c.key ? (
												<SortableHeaderCell key={c.label} sortKey={c.key} activeKey={sortKey} dir={sortDir} onSort={onSort} align={c.align}>
													{c.label}
												</SortableHeaderCell>
											) : (
												<TableCell key={c.label}>{c.label}</TableCell>
											),
										)}
									</TableRow>
								</TableHead>
								<TableBody>
									{pageRows.map((row) => (
										<TableRow key={row.id} className="itBudgetClickableRow" onClick={() => setOpenId(row.id)}>
											<TableCell>
												{fmtDay(row.received)}
												<div className="itBudgetMuted">{row.source === "manual_upload" ? "Upload" : (row.mailbox || "").replace(/@.*/, "@")}</div>
											</TableCell>
											<TableCell>{fmtDay(row.invoice_date)}</TableCell>
											<TableCell>
												<b>{row.vendor || "—"}</b>
											</TableCell>
											<TableCell className="itBudgetMono">{row.invoice_number || "—"}</TableCell>
											<TableCell align="right">{fmtUSD(row.total_amount)}</TableCell>
											<TableCell align="right" className="itBudgetMuted">
												{fmtUSD(row.tax_amount)}
											</TableCell>
											<TableCell>{row.card_label || row.payment_method}</TableCell>
											<TableCell>{row.budget_line || "—"}</TableCell>
											<TableCell>
												<span style={pillStyle(STATUS_PILLS[row.status] || STATUS_PILLS.New)}>{row.shadow ? "Shadow" : row.status}</span>
											</TableCell>
											<TableCell>{row.match ? <span style={pillStyle(MATCH_PILLS[row.match])}>{row.match}</span> : "—"}</TableCell>
											<TableCell>
												{row.dropbox_file_id ? (
													<Button size="small" startIcon={<PictureAsPdfIcon />} onClick={(e) => openFile(row, e)} sx={{ textTransform: "none" }}>
														{(row.dropbox_path || "").split("/").pop() || "Open"}
													</Button>
												) : (
													"—"
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
						<TanStackTablePagination table={pager} pagination={pagination} />
						<div className="itBudgetMuted">Showing {sorted.length} invoices · Provisional = card receipt logged before its statement arrives</div>
					</>
				)}
			</div>

			<InvoiceDialog
				open={openId !== null}
				invoiceId={openId}
				canEdit={canEdit}
				onClose={() => setOpenId(null)}
				onSaved={(message) => {
					setToast(message);
					load();
				}}
			/>
			<UploadInvoiceDialog
				open={uploadOpen}
				lookups={lookups}
				onClose={() => setUploadOpen(false)}
				onSaved={() => {
					setUploadOpen(false);
					setToast("Invoice uploaded and logged.");
					load();
				}}
			/>
			<Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<Alert severity="success" onClose={() => setToast("")}>
					{toast}
				</Alert>
			</Snackbar>
		</div>
	);
};

export default InvoiceLog;
